import React, { useState, useEffect } from "react";
import { auth, db } from "./firebaseConfig";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./ViewStudents.css";

const ViewStudents = ({ user }) => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState("displayName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [teacherCode, setTeacherCode] = useState("");
  const [darkMode, setDarkMode] = useState(() => {
    // Get dark mode preference from localStorage or default to false
    const savedMode = localStorage.getItem("darkMode");
    return savedMode ? JSON.parse(savedMode) : false;
  });

  // Check if user is logged in and is a teacher
  useEffect(() => {
    if (!user) {
      navigate("/");
    } else if (user.role !== "teacher") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Fetch teacher code and students
  useEffect(() => {
    const fetchTeacherCodeAndStudents = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // First get the teacher's code
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
          throw new Error("Teacher profile not found");
        }
        
        const userData = userDoc.data();
        const teacherCode = userData.teacherCode;
        setTeacherCode(teacherCode);
        
        if (!teacherCode) {
          throw new Error("Teacher code not found");
        }

        // Fetch students with that code in their teacherCodes array
        const usersRef = collection(db, "users");
        const q = query(
          usersRef, 
          where("role", "==", "student"),
          where("teacherCodes", "array-contains", teacherCode)
        );
        const querySnapshot = await getDocs(q);
        
        const studentsList = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          studentsList.push({
            id: doc.id,
            displayName: data.displayName || data.username || "Unknown",
            email: data.email || "No email",
            createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
          });
        });
        
        setStudents(studentsList);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching students:", error);
        setError(error.message || "Error fetching students");
        setIsLoading(false);
      }
    };

    if (user?.uid) {
      fetchTeacherCodeAndStudents();
    }
  }, [user]);

  // Sort students based on current sort field and direction
  const sortedStudents = [...students].sort((a, b) => {
    let valueA = a[sortField];
    let valueB = b[sortField];
    
    // Handle date comparison
    if (sortField === "createdAt") {
      valueA = new Date(valueA).getTime();
      valueB = new Date(valueB).getTime();
    }
    
    if (sortDirection === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });

  // Sort handler function
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (field !== sortField) return null;
    return sortDirection === "asc" ? "↑" : "↓";
  };

  return (
    <div className={`view-students-container ${darkMode ? 'dark-mode' : ''}`}>
      <div className="students-card">
        <div className="students-header">
          <h1 className="students-title">My Students</h1>
          <p className="students-subtitle">
            Students who have joined your class using code: <span className="teacher-code">{teacherCode}</span>
          </p>
        </div>
        
        <div className="students-content">
          {/* Back to Dashboard button */}
          <div 
            className="back-link"
            onClick={() => navigate("/dashboard")}
          >
            ← Back to Dashboard
          </div>
          
          {/* Loading state */}
          {isLoading && (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading students...</p>
            </div>
          )}
          
          {/* Error state */}
          {error && !isLoading && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {/* Empty state */}
          {!isLoading && !error && students.length === 0 && (
            <div className="empty-state">
              <p>No students have joined your class yet.</p>
              <p className="empty-state-sub">Share your teacher code with students so they can join your class.</p>
            </div>
          )}
          
          {/* Students table */}
          {!isLoading && !error && students.length > 0 && (
            <div className="students-table-container">
              <table className="students-table">
                <thead>
                  <tr>
                    <th 
                      onClick={() => handleSort("displayName")}
                      className={sortField === "displayName" ? "active-sort" : ""}
                    >
                      Display Name {getSortIndicator("displayName")}
                    </th>
                    <th 
                      onClick={() => handleSort("email")}
                      className={sortField === "email" ? "active-sort" : ""}
                    >
                      Email {getSortIndicator("email")}
                    </th>
                    <th 
                      onClick={() => handleSort("createdAt")}
                      className={sortField === "createdAt" ? "active-sort" : ""}
                    >
                      Date Joined {getSortIndicator("createdAt")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStudents.map((student) => (
                    <tr key={student.id}>
                      <td>
                        <span 
                          className="student-name-link"
                          onClick={() => navigate(`/student-profile/${student.id}`)}
                        >
                          {student.displayName}
                        </span>
                      </td>
                      <td>{student.email}</td>
                      <td>{formatDate(student.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewStudents;
